import { memo, useCallback, useContext, useEffect, useRef } from 'react'
import { useLocation } from 'react-router-dom'
import { QueryClient, QueryClientProvider } from 'react-query'
import { FlexBox, SearchIcon, TextInput } from '@fable/components'
import { css, cx, injectGlobalMod, useTheme } from '@fable/theme'
import { SearchContext } from 'state'
import close from 'assets/icons/standard/close-icon-grey-bg.svg'

interface Props {
  onClick?: () => void
  defaultFilter?: 'book' | 'club' | ''
  onInputCleared?: () => void
  className?: string
}

const SearchbarWithProvider = ({
  onClick,
  defaultFilter = '',
  className,
}: Props) => {
  const { setInput, setActiveFilter, searchbarFocus, input, activeFilter } =
    useContext(SearchContext)
  const location = useLocation()
  const { nativeOverrides, colors } = useTheme()

  const searchbarRef = useRef<HTMLInputElement | null>(null)
  const flushGlobalStyles = useRef<() => void>(() => null)
  const canScroll = useRef(false)

  const handleChangeText = (s: string) => {
    setInput(s)
  }

  const updateDocumentScroll = useCallback(() => {
    if (!!input && canScroll.current) {
      flushGlobalStyles.current = injectGlobalMod`body,html{overflow: hidden;}`
      canScroll.current = false
    } else if (!input?.length && !canScroll.current) {
      flushGlobalStyles.current()
      canScroll.current = true
    }
  }, [input])

  useEffect(() => {
    updateDocumentScroll()
  }, [updateDocumentScroll])

  useEffect(() => {
    const params = new URLSearchParams(location.search)
    const query = params.get('q')
    const type = params.get('type')

    if (!input && !!query) {
      setInput(query)

      if (!activeFilter && (type === 'club' || type === 'book')) {
        setActiveFilter(type)
      }
    }
  }, [activeFilter, input, location.search, setActiveFilter, setInput])

  useEffect(() => {
    if (searchbarFocus) {
      searchbarRef.current?.focus()
    }
  }, [searchbarFocus])

  useEffect(() => {
    return () => {
      if (flushGlobalStyles.current) {
        flushGlobalStyles.current()
      }
    }
  }, [])

  const determinePlaceholder = () => {
    switch (defaultFilter) {
      case 'book':
        return 'Shop 1M+ ebooks...'
      case 'club':
        return 'Search by club name, moderator, or book'
      default:
        return 'Clubs, books, authors...'
    }
  }

  return (
    <div
      onClick={onClick}
      className={cx(
        css`
          height: 52px;
          border-radius: 150px;
          background: white;
        `,
        className
      )}
    >
      <TextInput
        inputRef={searchbarRef}
        buttonContent={
          <FlexBox
            className={css`
              width: 40px;
              height: 40px;
              background-color: ${colors.uiBlack};
              border-radius: 50%;
            `}
            centerAll
          >
            <SearchIcon fill={colors.uiWhite} />
          </FlexBox>
        }
        buttonPosition="right"
        theme="light"
        placeholder={determinePlaceholder()}
        value={input}
        onChangeText={handleChangeText}
        autoComplete="off"
        name="book-search"
        containerClass={css`
          height: 52px;
          border-radius: 150px;
          padding: 0 6px;
          box-shadow: 0px 4px 50px rgba(0, 0, 0, 0.05);
        `}
      >
        {!!input?.length && (
          <button
            onClick={() => setInput('')}
            className={cx(
              nativeOverrides.buttonClear,
              css`
                margin-right: 6px;
                height: 20px;
                width: 20px;
                opacity: 0.5;
              `
            )}
          >
            <img src={close} alt="X in a circle. Click to clear search bar" />
          </button>
        )}
      </TextInput>
    </div>
  )
}

const queryClient = new QueryClient()

const Searchbar = memo(
  (props: Props) => (
    <QueryClientProvider client={queryClient}>
      <SearchbarWithProvider {...props} />
    </QueryClientProvider>
  ),
  // Prevents re-rendering while scrolling due to header re-rendering
  (prevProps, nextProps) => prevProps.className === nextProps.className
)

export default Searchbar
